import { useReactiveVar } from "@apollo/client";
import { ErrorBoundary } from "@sentry/react";
import { useCallback, useContext, useMemo } from "react";
import {
  MeetingViewMeetingNodeNewPageFragmentFragment,
  TopicNodeNewPageFragmentFragment,
} from "types/graphql-schema";

import { currentTiptapJWTVar } from "@cache/cache";
import Error from "@components/error/error";
import Loading from "@components/loading/loading";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import WYSIWYG from "@components/wysiwyg/wysiwyg";
import { tempCacheIdPrefix } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { parseStringToJSON } from "@helpers/helpers";

import { MeetingWebsocketProviderContext } from "../context";
import { meetingPaddingClassName } from "../helpers";

const DiscussionNotes = ({
  topic,
  meeting,
  className,
  onContentChange,
  hasContent,
  onModEnter,
}: {
  topic: TopicNodeNewPageFragmentFragment;
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  className?: string;
  onContentChange?: (hasContent: boolean) => void;
  hasContent?: boolean;
  onModEnter?: () => void;
}) => {
  const isTemporary = String(topic.id).includes(tempCacheIdPrefix);

  const topicValue = parseStringToJSON(topic.discussionNotes);
  let overlayValue = null;
  if (topic.linkedTemplateTopic?.id) {
    if (isEmptyValue(topicValue) && topic.linkedTemplateTopic.discussionNotes) {
      overlayValue = parseStringToJSON(
        topic.linkedTemplateTopic.discussionNotes,
      );
    }
  }

  const meetingGroupId = meeting.meetingGroup?.id;
  const organizationId = meeting.organization?.id;
  const uploadVariable = useMemo(() => ({ topicId: topic.id }), [topic.id]);

  const mentionsConfig = useMemo(
    () => ({
      meetingGroupId: meetingGroupId,
      meetingId: meeting.id,
    }),
    [meeting.id, meetingGroupId],
  );
  const extraContext = useMemo(
    () => ({
      topicId: topic.id,
      relatedTopicId: topic.id,
      meetingId: meeting.id,
      meetingDate: meeting.startDatetime || undefined,
      meetingGroupId: meetingGroupId || undefined,
      organizationId: organizationId,
    }),
    [
      meeting.id,
      topic.id,
      meetingGroupId,
      meeting.startDatetime,
      organizationId,
    ],
  );

  const { sharedNotes } = useContext(MeetingWebsocketProviderContext);
  const tiptapAiJwt = useReactiveVar(currentTiptapJWTVar);

  const handleUpdateContent = useCallback(
    ({ editor }: { editor: any }) => {
      if (onContentChange) {
        onContentChange(!isEmptyValue(editor.getJSON()));
      }
    },
    [onContentChange],
  );

  return (
    <div
      aria-label="Meeting topic shared notes"
      className={classNames(
        "relative transition-[padding] duration-200 ease-in-out",
        hasContent && "pb-10",
      )}
    >
      <div>
        {isTemporary ? (
          <div className="min-h-8 flex items-center justify-center">
            <Loading size="5" />
          </div>
        ) : (
          <ErrorBoundary
            fallback={
              <Error
                title="An unexpected error occurred."
                description={
                  "The editor could not be rendered. We have been notified. Please refresh the page."
                }
              />
            }
          >
            <div
              id={`editor-topic-${topic.id}`}
              // Do not remove the key with tiptapAiJwt, editor needs to be recreated when the tiptapAiJwt changes
              key={`${tiptapAiJwt}`}
            >
              <WYSIWYG
                key={`topic-${topic.id}`}
                value={topicValue}
                enableComment={topic.canUpdate.permission}
                className={classNames(
                  "text-base",
                  // important to have padding so the +/drag hover buttons on each line is clickable and we can detect correctly when user's mouse leaves the editor.
                  // the buttons need to be inside the editor.
                  "pt-0.5 pb-0",
                  meetingPaddingClassName,
                  topic.canUpdate.permission && "min-h-8",
                  className,
                )}
                ydoc={sharedNotes.ydoc}
                providerWebsocket={sharedNotes.providerWebsocket}
                editable={topic.canUpdate.permission}
                placeholder="Press '/' for commands, 'Cmd+Enter' for new topic"
                showPlusButton={topic.canUpdate.permission}
                uploadVariable={uploadVariable}
                overlayValue={overlayValue}
                webSocketDocumentId={`topic-${topic.id}`}
                mentionsConfig={mentionsConfig}
                extraContext={extraContext}
                tiptapAiJwt={tiptapAiJwt}
                onUpdateContent={
                  onContentChange ? handleUpdateContent : undefined
                }
                onModEnter={onModEnter}
              />
            </div>
          </ErrorBoundary>
        )}
      </div>
    </div>
  );
};

export default DiscussionNotes;
